/**
 * `/v1/subscription` — the read, the first purchase, and the switch between paid tiers.
 */
import { z } from "zod";
import { BILLING_INTERVALS, CHECKOUT_TIERS, SUBSCRIPTION_STATUSES } from "../billing.js";
import { BILLING_STATES, TIERS } from "../tiers.js";
/** A first purchase: a tier Checkout can sell, and how often it bills. */
export const checkoutRequestSchema = z
    .object({
    tier: z.enum(CHECKOUT_TIERS, {
        error: "Choose Starter, Growth or Scale. Launch is free and Enterprise is quoted.",
    }),
    interval: z.enum(BILLING_INTERVALS, { error: "Choose monthly or yearly billing." }),
})
    .strict();
/** Where the browser goes next: Stripe's hosted page. */
export const checkoutResponseSchema = z.object({ url: z.url() });
export const subscriptionViewSchema = z.object({
    plan: z.enum(TIERS),
    billingStatus: z.enum(BILLING_STATES),
    /** The newest subscription this organisation has held, or `null` before the first. */
    subscription: z
        .object({
        stripeSubscriptionId: z.string(),
        plan: z.enum(TIERS),
        status: z.enum(SUBSCRIPTION_STATUSES),
        periodStart: z.iso.datetime(),
        periodEnd: z.iso.datetime(),
        gtinLimit: z.number().int(),
        requestLimit: z.number().int(),
        /** Cancelled in the portal: the subscription ends at `periodEnd` and bills nothing after. */
        cancelAtPeriodEnd: z.boolean(),
    })
        .nullable(),
    /** Published products — the count a lower tier's `activeGtins` is compared against. */
    gtinsActive: z.number().int(),
});
const switchTier = z.enum(CHECKOUT_TIERS, {
    error: "Choose Starter, Growth or Scale. Launch is a cancellation and Enterprise is quoted.",
});
/** A switch keeps the subscription's billing interval, so it names the tier alone. */
export const switchRequestSchema = z.object({ tier: switchTier }).strict();
export const switchPreviewQuerySchema = z.object({ tier: switchTier }).strict();
/**
 * What a switch costs, from Stripe's invoice preview. Amounts are in the currency's minor
 * unit, as Stripe reports them. `null` when Stripe cannot preview it.
 */
export const switchPreviewSchema = z
    .object({
    /** Charged at the switch: the new tier's remaining days less the old tier's. */
    amountDueToday: z.number().int().nonnegative(),
    /** Unused time on a dearer tier, credited to later invoices. */
    creditToday: z.number().int().nonnegative(),
    currency: z.string(),
    nextRenewal: z.object({ at: z.iso.datetime(), amount: z.number().int() }),
})
    .nullable();
/** Where the browser goes next: Stripe's hosted plan-change page. */
export const switchResponseSchema = z.object({ url: z.url() });
/**
 * The billing portal for something other than a switch: cancelling at period end, or
 * updating the card behind a payment that failed. Drift Log §U60.
 */
export const MANAGE_INTENTS = ["cancel", "payment"];
export const manageRequestSchema = z
    .object({
    intent: z.enum(MANAGE_INTENTS, {
        error: "Say whether this is a cancellation or a payment update.",
    }),
})
    .strict();
export const manageResponseSchema = z.object({ url: z.url() });
/** "Let it lapse": when the trial now ends. The trial clock moves the plan — `ops/trial.ts`. */
export const endTrialResponseSchema = z.object({ trialEndsAt: z.iso.datetime() });
/** The opt-in hard cap on active GTINs, off by default — `ops/billing.ts`'s `setHardCap`. */
export const hardCapSchema = z
    .object({ hardCap: z.boolean({ error: "hardCap must be true or false." }) })
    .strict();
const CONTACT_EMAIL_MESSAGE = "That is not an email address we can send an invoice to.";
/**
 * Where Stripe sends invoices and payment-failure mail, read through to Stripe on render.
 *
 * Not a column: no second copy to drift, and Drift Log §U60's "nothing stores it locally"
 * stays true — Fable plan §6 Q1.
 *
 * `null` is an organisation with no Stripe customer, so no invoice address exists at all.
 * `{ email: null }` is a customer carrying none.
 */
export const billingContactSchema = z.object({ email: z.string().nullable() }).nullable();
export const setBillingContactSchema = z
    .object({
    email: z
        .string(CONTACT_EMAIL_MESSAGE)
        .transform((raw) => raw.trim().toLowerCase())
        .pipe(z.email(CONTACT_EMAIL_MESSAGE)),
})
    .strict()
    .describe("PUT /v1/subscription/contact — the address Stripe invoices.");
//# sourceMappingURL=billing.js.map